import '@salesforce/canvas-js-sdk'
import { canvasAuthService } from '../../../common/services/CanvasAuthService'
import {
  CanvasResizeSize,
  NavigateToUrlOptions,
  SfActionType,
} from '../types/salesforce'

/**
 * Service for publishing events to parent Salesforce window via postMessage
 *
 * All events use the 'labra.canvas.' prefix for namespace consistency
 *
 * Canvas apps are embedded in Salesforce, so we use window.top.postMessage
 * to communicate with the parent Lightning page
 */
class CanvasSdkService {
  /**
   * Check if window.top is available for postMessage communication
   */
  isAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.top !== 'undefined'
  }

  /**
   * Get the location URL for secure postMessage communication
   * Returns null if locationUrl is not available
   */
  private getLocationUrl(): string | null {
    return canvasAuthService.getLocationUrl()
  }

  /**
   * Get the appropriate window target for postMessage
   */
  private getPostMessageTarget(): Window | null {
    return window.top ?? null
  }

  /**
   * Resolve the origin of the top-level (Lightning) window for a secure
   * postMessage `targetOrigin`.
   *
   * When embedded via the native Salesforce package the Canvas app is nested
   * (Lightning page → LWC iframe → Visualforce page → Canvas iframe), so
   * `locationUrl` points at the Visualforce domain, NOT the top window. The
   * browser drops a `window.top` post whose targetOrigin doesn't match the top
   * origin, so we read the real top origin from `location.ancestorOrigins`
   * (ordered parent → top; the last entry is the top-level Lightning origin).
   *
   * `ancestorOrigins` is unsupported in Firefox — falls back to '*' (the LWC
   * still validates the sender origin on receipt).
   */
  private getTopOrigin(): string {
    try {
      const ancestors = window.location.ancestorOrigins
      if (ancestors && ancestors.length > 0) {
        return ancestors[ancestors.length - 1]
      }
    } catch {
      // ancestorOrigins not supported (e.g. Firefox)
    }
    return '*'
  }

  /**
   * Builds base context object for error logging
   * Contains common fields used across all error logs in this service
   */
  private buildErrorContext(
    additionalContext?: Record<string, unknown>
  ): Record<string, unknown> {
    return {
      partnerId: canvasAuthService.getPartnerId(),
      crmId: canvasAuthService.getCrmId(),
      isInitialized: canvasAuthService.isInitialized(),
      ...additionalContext,
    }
  }

  /**
   * Validates that a URL matches expected Salesforce domain patterns
   * This is a defense-in-depth measure since instanceUrl comes from signed Canvas envelope
   */
  private isValidSalesforceUrl(url: string): boolean {
    try {
      const parsed = new URL(url)
      const hostname = parsed.hostname.toLowerCase()

      const validPatterns = [
        /\.salesforce\.com$/,
        /\.force\.com$/,
        /\.salesforce-sites\.com$/,
        /\.my\.salesforce\.com$/,
        /\.lightning\.force\.com$/,
        /\.visualforce\.com$/,
      ]

      return validPatterns.some(pattern => pattern.test(hostname))
    } catch {
      return false
    }
  }

  /**
   * Publishes an event to the parent Salesforce window
   * @private
   */
  private publishEvent(
    name: string,
    payload: Record<string, unknown>,
    targetOriginOverride?: string
  ): void {
    if (!this.isAvailable()) {
      console.warn('Canvas SDK: window.top not available, cannot publish event')
      return
    }

    let targetOrigin: string
    if (targetOriginOverride) {
      // Caller resolved the target origin itself (e.g. the top Lightning origin
      // via ancestorOrigins for the native-package nesting).
      targetOrigin = targetOriginOverride
    } else {
      const locationUrl = this.getLocationUrl()
      if (!locationUrl) {
        console.error(
          `Canvas SDK: Cannot publish event '${name}' - locationUrl not available. This is required for secure postMessage communication.`
        )
        return
      }
      try {
        targetOrigin = new URL(locationUrl).origin
      } catch {
        const context = this.buildErrorContext({ locationUrl, eventName: name })
        console.error(
          `Canvas SDK: Cannot publish event '${name}' - locationUrl is not a valid URL.`,
          { context }
        )
        return
      }
    }

    const canvasId = canvasAuthService.getCanvasId()
    if (!canvasId) {
      const context = this.buildErrorContext({ eventName: name })
      console.error(
        `Canvas SDK: Cannot publish event '${name}' - canvasId not available. This is required for secure postMessage communication.`,
        { context }
      )
      return
    }

    const postMessageTarget = this.getPostMessageTarget()
    if (!postMessageTarget) {
      const context = this.buildErrorContext({
        eventName: name,
        isVFPage: canvasAuthService.getCustomParameters()?.isVFPage,
        framesLength: window.top?.frames?.length,
      })
      console.error(
        `Canvas SDK: Cannot publish event '${name}' - no valid postMessage target available.`,
        { context }
      )
      return
    }

    try {
      const message = {
        name: `labra.canvas.${name}`,
        canvasId,
        payload: {
          timestamp: new Date().toISOString(),
          ...payload,
        },
      }

      postMessageTarget.postMessage(message, targetOrigin)
    } catch (error) {
      console.error(`Error publishing event: labra.canvas.${name}`, error)
    }
  }

  /**
   * True when the URL already carries a Canvas re-entry target in its hash
   * (or as a query param). Case-insensitive; avoids double-wrapping.
   */
  private hasCanvasParamTargetURL(url: string): boolean {
    try {
      const parsed = new URL(url, 'https://placeholder.invalid')
      const hash = parsed.hash.startsWith('#')
        ? parsed.hash.slice(1)
        : parsed.hash
      const hashParams = new URLSearchParams(hash)
      for (const key of hashParams.keys()) {
        if (key.toLowerCase() === 'canvasparamtargeturl') {
          return true
        }
      }
      for (const key of parsed.searchParams.keys()) {
        if (key.toLowerCase() === 'canvasparamtargeturl') {
          return true
        }
      }
    } catch {
      // Fall through to regex for non-URL strings
    }
    return /(?:^|[?#&])canvasparamtargeturl=/i.test(url)
  }

  /**
   * Build a Lightning tab URL that re-enters Canvas with the SPA target in the
   * hash. Forces a fresh signed_request POST so the modal gets a new exchange token.
   */
  private toCanvasReentryUrl(url: string): string | null {
    if (this.hasCanvasParamTargetURL(url)) {
      return url
    }

    const instanceUrl = canvasAuthService.getInstanceUrl()
    if (!instanceUrl || !this.isValidSalesforceUrl(instanceUrl)) {
      return null
    }

    const encodedUrl = encodeURIComponent(url)
    const fullUrl = `${instanceUrl}/lightning/n/LabraIo__Labra#canvasParamTargetURL=${encodedUrl}`
    if (!fullUrl.startsWith(instanceUrl)) {
      return null
    }
    return fullUrl
  }

  /**
   * Request parent to open a modal.
   * Rewrites Labra SPA URLs to Canvas re-entry Lightning URLs when possible so
   * the Salesforce package opens a fresh Canvas handshake instead of a bare
   * SPA iframe (which has no exchange_token).
   *
   * Never rejects: callers fire-and-forget this from click handlers.
   * @param url - URL to open in the modal
   * @param label - Title/label for the modal
   */
  async openModal(url: string, label: string): Promise<void> {
    try {
      const ok = await canvasAuthService.ensureFreshSession()
      if (!ok) {
        console.warn(
          'Canvas SDK: openModal skipped because the Salesforce session is unrecoverable'
        )
        return
      }
      const modalUrl = this.toCanvasReentryUrl(url) ?? url
      const isNativeSalesforcePackage =
        canvasAuthService.getCustomParameters()?.isNativeSalesforcePackage
      this.publishEvent(
        'openModal',
        { url: modalUrl, label },
        isNativeSalesforcePackage ? this.getTopOrigin() : undefined
      )
    } catch (error) {
      // Call sites fire-and-forget openModal(); never reject so a failed
      // refresh cannot become an unhandled promise.
      console.warn('Canvas SDK: openModal failed', error)
    }
  }

  /**
   * Navigate to a URL by opening it in a new browser tab/window.
   *
   * By default uses window.open(). When `options.viaSalesforce` is true, navigates
   * via Sfdc.canvas.client.publish() / postMessage so the parent Salesforce page
   * controls the navigation behavior.
   *
   * @param url - URL path to navigate to (will be encoded and added as hash parameter)
   * @param options - Optional navigation options
   * @param options.target - Navigation target (_blank, _self, etc.)
   * @param options.targetTabApiName - Salesforce tab API name (defaults to 'LabraIo__Labra')
   * @param options.viaSalesforce - When true, navigate via Salesforce canvas client instead of window.open()
   * @returns The opened Window reference, or null if navigation failed, was blocked, or viaSalesforce was used
   */
  navigateToUrl(url: string, options?: NavigateToUrlOptions): Window | null {
    const {
      target,
      targetTabApiName = 'LabraIo__Labra',
      viaSalesforce,
    } = options || {}
    const instanceUrl = canvasAuthService.getInstanceUrl()
    if (!instanceUrl) {
      const context = this.buildErrorContext({
        targetUrl: url,
        targetTabApiName,
      })
      console.error(
        'Canvas SDK: Cannot navigate - instanceUrl not available. This is required for building Salesforce navigation URLs.',
        { context }
      )
      return null
    }

    if (!this.isValidSalesforceUrl(instanceUrl)) {
      const context = this.buildErrorContext({
        instanceUrl,
        targetUrl: url,
        targetTabApiName,
      })
      console.error(
        'Canvas SDK: Cannot navigate - instanceUrl does not match expected Salesforce domain patterns.',
        { context }
      )
      return null
    }

    // Validate targetTabApiName to prevent path traversal
    const validTabNamePattern = /^[a-zA-Z0-9_]+$/
    if (!validTabNamePattern.test(targetTabApiName)) {
      const context = this.buildErrorContext({
        instanceUrl,
        targetUrl: url,
        targetTabApiName,
      })
      console.error(
        'Canvas SDK: Cannot navigate - invalid targetTabApiName. Must contain only alphanumeric characters and underscores.',
        { context }
      )
      return null
    }

    const encodedUrl = encodeURIComponent(url)
    const fullUrl = `${instanceUrl}/lightning/n/${targetTabApiName}#canvasParamTargetURL=${encodedUrl}`

    // Validate constructed URL starts with validated instanceUrl to prevent path traversal
    if (!fullUrl.startsWith(instanceUrl)) {
      const context = this.buildErrorContext({
        instanceUrl,
        targetUrl: url,
        fullUrl,
      })
      console.error(
        'Canvas SDK: Cannot navigate - constructed URL does not start with validated instanceUrl.',
        { context }
      )
      return null
    }

    if (viaSalesforce) {
      // canvasClient contains sensitive tokens (oauthToken, refreshToken) - never log directly
      const canvasClient = canvasAuthService.getCanvasClient()
      if (
        canvasClient &&
        typeof Sfdc !== 'undefined' &&
        typeof Sfdc?.canvas?.client?.publish === 'function'
      ) {
        Sfdc.canvas.client.publish(canvasClient, {
          name: 'labra.navigateToUrl',
          payload: { url: fullUrl, target: target || '_blank' },
        })
        return null
      } else {
        const context = this.buildErrorContext({
          hasCanvasClient: !!canvasClient,
          hasSfdc: typeof Sfdc !== 'undefined',
        })
        console.warn(
          'Canvas SDK: Sfdc.canvas.client not available, falling back to window.open.',
          { context }
        )
      }
    }

    const openedWindow = window.open(fullUrl, target || '_blank')
    if (!openedWindow) {
      const context = this.buildErrorContext({
        targetUrl: url,
        fullUrl,
        targetTabApiName,
      })
      console.error(
        'Canvas SDK: window.open() returned null - popup may have been blocked by the browser.',
        { context }
      )
    }
    return openedWindow
  }

  /**
   * Navigate to a Salesforce record by opening its URL directly via window.open().
   *
   * The objectType parameter was intentionally removed from the signature
   * because Salesforce record URLs don't require it — the record ID alone
   * is sufficient for navigation.
   *
   * @param recordId - Salesforce record ID (e.g., 006xxx)
   * @param target - Optional navigation target (_blank, _self, etc.)
   * @returns The opened Window reference, or null if navigation failed or was blocked
   */
  navigateToRecord(recordId: string, target?: string): Window | null {
    const instanceUrl = canvasAuthService.getInstanceUrl()
    if (!instanceUrl) {
      const context = this.buildErrorContext({ recordId })
      console.error(
        'Canvas SDK: Cannot navigate to record - instanceUrl not available.',
        { context }
      )
      return null
    }

    const recordUrl = `${instanceUrl}/${recordId}`
    const openedWindow = window.open(recordUrl, target || '_blank')
    if (!openedWindow) {
      const context = this.buildErrorContext({ recordId, recordUrl })
      console.error(
        'Canvas SDK: window.open() returned null - popup may have been blocked by the browser.',
        { context }
      )
    }
    return openedWindow
  }

  /**
   * Navigate back in browser history
   */
  navigateBack(): void {
    history.back()
  }

  /**
   * Request parent Salesforce page to refresh
   */
  refreshParent(): void {
    this.publishEvent('refreshParent', {})
  }

  /**
   * Request parent to close the modal
   */
  closeModal(): void {
    this.publishEvent('closeModal', {})
  }

  /**
   * Ask the host Salesforce page (LWC) to run a named action.
   * Payload is `{ action }` — e.g. `open_sf_cosell` to open the Co-sell dropdown.
   */
  triggerAction(action: SfActionType): void {
    const isNativeSalesforcePackage =
      canvasAuthService.getCustomParameters()?.isNativeSalesforcePackage
    this.publishEvent(
      'triggerAction',
      { action },
      isNativeSalesforcePackage ? this.getTopOrigin() : undefined
    )
  }

  /**
   * Resize the Canvas iframe within Salesforce.
   *
   * Only affects the iframe when the app is embedded as a Canvas iframe in a
   * Lightning/VF page. Has no effect for content rendered in a Salesforce modal
   * (use openModal/closeModal for that) and requires Sfdc.canvas.client to be
   * available.
   *
   * @param size - e.g. { height: '800px', width: '100%' }. Omit a dimension to leave it unchanged.
   */
  resize(size: CanvasResizeSize): void {
    // 1) Resize the inner Canvas iframe (best-effort).
    const canvasClient = canvasAuthService.getCanvasClient()
    if (
      canvasClient &&
      typeof Sfdc !== 'undefined' &&
      typeof Sfdc?.canvas?.client?.resize === 'function'
    ) {
      // The SDK's @types marks both height/width as required, but the runtime
      // (and its own JSDoc) supports partial sizes — omitting a dimension leaves
      // it unchanged. Cast to satisfy the overly strict type.
      Sfdc.canvas.client.resize(
        canvasClient,
        size as { height: string; width: string }
      )
    }

    // 2) In the native Salesforce package the Canvas is embedded in a
    // fixed-height LWC iframe, so notify that host (at the top Lightning window)
    // to resize the outer iframe to match content. Post to window.top with the
    // real top origin (locationUrl points at the intermediate Visualforce
    // domain, which the browser would reject). Uses the same postMessage channel
    // as openModal/closeModal.
    const isNativeSalesforcePackage =
      canvasAuthService.getCustomParameters()?.isNativeSalesforcePackage
    if (isNativeSalesforcePackage) {
      this.publishEvent(
        'resize',
        { height: size.height, width: size.width },
        this.getTopOrigin()
      )
    }
  }

  /**
   * Enable or disable automatic resizing of the Canvas iframe to fit its content.
   *
   * Same embedding constraints as resize(): requires Sfdc.canvas.client and only
   * affects a Canvas iframe within a Lightning/VF page.
   *
   * @param enabled - true to start auto-resizing, false to stop
   * @param intervalMs - Optional polling interval in milliseconds
   */
  autogrow(enabled: boolean, intervalMs?: number): void {
    const canvasClient = canvasAuthService.getCanvasClient()
    if (
      canvasClient &&
      typeof Sfdc !== 'undefined' &&
      typeof Sfdc?.canvas?.client?.autogrow === 'function'
    ) {
      Sfdc.canvas.client.autogrow(canvasClient, enabled, intervalMs)
    } else {
      const context = this.buildErrorContext({
        hasCanvasClient: !!canvasClient,
        hasSfdc: typeof Sfdc !== 'undefined',
        enabled,
      })
      console.warn('Canvas SDK: autogrow not available.', { context })
    }
  }
}

// Export singleton instance
export const canvasSdkService = new CanvasSdkService()
